import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';
import axios from 'axios';
import utils from '@/utils';
import {
  EXTERNAL_SERVICE,
  EXTERNAL_SERVICE_ACTION
} from '@/analytics/constants';
import { captureAndRethrowExternalServiceError } from '@/analytics/reportExternalServiceError';

const FirebasePlugin = {
  install(Vue) {
    firebase.initializeApp({
      apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
      authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET
    });

    const db = firebase.firestore();
    db.settings({
      experimentalForceLongPolling: true
    });

    Vue.prototype.$firebase = new Vue({
      data: {
        db,
        storage: firebase.storage(),
        listeners: []
      },
      methods: {
        getCurrentUser() {
          return firebase.auth().currentUser;
        },
        getRestoredUser() {
          // 前回のセッションの復元が終わるまでcurrentUserはnullのため、最初の通知を待つ
          return new Promise(resolve => {
            const unsubscribe = firebase.auth().onAuthStateChanged(
              user => {
                unsubscribe();
                resolve(user);
              },
              () => {
                unsubscribe();
                resolve(null);
              }
            );
          });
        },
        getToken(idToken, roleName) {
          let url = `${process.env.VUE_APP_FIREBASE_CLOUDFUNCTIONS_API}/auth/${roleName}`;
          return axios.get(url, {
            headers: {
              Authorization: 'Bearer ' + idToken
            }
          });
        },
        setToken(token) {
          return firebase.auth().signInWithCustomToken(token);
        },
        getTimestamp(seconds, nanoseconds) {
          return new firebase.firestore.Timestamp(seconds, nanoseconds);
        },
        increment(value) {
          return firebase.firestore.FieldValue.increment(value);
        },
        updateProfile(user) {
          const currentUser = firebase.auth().currentUser;
          if (
            currentUser.displayName === user.name &&
            currentUser.photoURL === user.picture
          ) {
            return Promise.resolve();
          }
          return currentUser.updateProfile({
            displayName: user.name,
            photoURL: user.picture
          });
        },
        signOut() {
          this.resetListeners();
          return firebase.auth().signOut();
        },
        createRoom(params) {
          const data = {
            company_uid: params.companyId,
            company_last_datetime: params.datetime,
            company_manual_unread_datetime: null,
            company_manual_unread_message_id: null,
            company_total_unread: 0,
            influencer_uid: params.influencerId,
            influencer_last_datetime: params.datetime,
            influencer_total_unread: 0,
            project_id: params.projectId
          };
          return this.setRoomData(params.id, data);
        },
        getRoomData(docName) {
          const ref = this.db.collection('rooms').doc(docName);
          return ref.get();
        },
        setRoomData(docName, data) {
          const ref = this.db.collection('rooms').doc(docName);
          return ref
            .set(data, { merge: true })
            .catch(
              captureAndRethrowExternalServiceError(
                EXTERNAL_SERVICE.FIRESTORE,
                EXTERNAL_SERVICE_ACTION.CHAT_ROOM_CREATE
              )
            );
        },
        updateRoomData(docName, data) {
          const ref = this.db.collection('rooms').doc(docName);
          return ref
            .update(data)
            .catch(
              captureAndRethrowExternalServiceError(
                EXTERNAL_SERVICE.FIRESTORE,
                EXTERNAL_SERVICE_ACTION.CHAT_ROOM_UPDATE
              )
            );
        },
        addMessage(docName, params, roomData) {
          const uid = firebase.auth().currentUser.uid;
          const roomRef = this.db.collection('rooms').doc(docName);
          const messageRef = roomRef.collection('messages').doc();
          const counterField =
            uid === roomData.company_uid
              ? 'influencer_total_unread'
              : 'company_total_unread';
          let data = {
            datetime: firebase.firestore.FieldValue.serverTimestamp(),
            isDeleted: false,
            isEdited: false,
            message: params.message,
            uid
          };
          if (params.hasOwnProperty('image')) {
            data.imageUrl = params.image.imageUrl;
            data.storageUri = params.image.storageUri;
            data.thumbnail = params.image.thumbnail;
          } else if (params.hasOwnProperty('file')) {
            data.fileType = params.file.fileType;
            data.fileUrl = params.file.fileUrl;
          }
          const batch = this.db.batch();
          batch.set(messageRef, data);
          batch.update(roomRef, {
            [counterField]: firebase.firestore.FieldValue.increment(1)
          });
          return batch
            .commit()
            .catch(
              captureAndRethrowExternalServiceError(
                EXTERNAL_SERVICE.FIRESTORE,
                EXTERNAL_SERVICE_ACTION.CHAT_MESSAGE_SEND
              )
            )
            .then(() => messageRef);
        },
        updateMessageData(roomId, messageId, data) {
          return this.db
            .collection(`rooms/${roomId}/messages`)
            .doc(messageId)
            .update(data)
            .catch(
              captureAndRethrowExternalServiceError(
                EXTERNAL_SERVICE.FIRESTORE,
                EXTERNAL_SERVICE_ACTION.CHAT_MESSAGE_UPDATE
              )
            );
        },
        getImageData(uri) {
          return this.storage.ref().child(uri);
        },
        putFile(ref, file, fileName) {
          const uid = firebase.auth().currentUser.uid;
          const filePath = `${uid}/${ref.id}/${fileName}`;
          return this.storage
            .ref(filePath)
            .put(file)
            .catch(
              captureAndRethrowExternalServiceError(
                EXTERNAL_SERVICE.FIREBASE_STORAGE,
                EXTERNAL_SERVICE_ACTION.CHAT_FILE_UPLOAD
              )
            )
            .then(snapshot => {
              return snapshot.ref
                .getDownloadURL()
                .catch(
                  captureAndRethrowExternalServiceError(
                    EXTERNAL_SERVICE.FIREBASE_STORAGE,
                    EXTERNAL_SERVICE_ACTION.CHAT_FILE_GET_URL
                  )
                )
                .then(url => {
                  const params = {
                    storageUri: snapshot.metadata.fullPath
                  };
                  if (utils.isImageType(file.type)) {
                    params.imageUrl = url;
                  } else {
                    params.fileUrl = url;
                  }
                  return ref
                    .update(params)
                    .catch(
                      captureAndRethrowExternalServiceError(
                        EXTERNAL_SERVICE.FIRESTORE,
                        EXTERNAL_SERVICE_ACTION.CHAT_MESSAGE_UPDATE
                      )
                    );
                });
            })
            .catch(error => {
              console.log(error);
            });
        },
        setListener(listener) {
          this.listeners.push(listener);
        },
        resetListeners() {
          this.listeners.forEach(listener => listener());
        }
      }
    });
  }
};

export default FirebasePlugin;
